import { motion } from "framer-motion";
import { LinkIcon } from "lucide-react";
import type { ChangeEvent, ReactNode } from "react";

import { fadeSlideVariants, springGentle } from "../share-session-dialog-constants";

import { Button } from "@/components/ui/button";
import { DialogClose, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const CREATE_SPINNER_TRANSITION = { repeat: Infinity, duration: 0.8, ease: "linear" } as const;

export interface ShareSessionCreateViewLayoutProps {
  title: string;
  description: string;
  namePlaceholder: string;
  name: string;
  onNameChange: (value: string) => void;
  /** Middle section: version selectors, toggles, etc. */
  settings: ReactNode;
  hasExistingSessions: boolean;
  isCreateDisabled: boolean;
  isPending: boolean;
  onCreate: () => void;
  onBack: () => void;
  /** Label on the primary button while the create request is in flight. */
  pendingButtonLabel?: string;
}

export const ShareSessionCreateViewLayout = ({
  title,
  description,
  namePlaceholder,
  name,
  onNameChange,
  settings,
  hasExistingSessions,
  isCreateDisabled,
  isPending,
  onCreate,
  onBack,
  pendingButtonLabel = "Creating…",
}: ShareSessionCreateViewLayoutProps) => {
  const handleSessionNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    onNameChange(event.target.value);
  };

  return (
    <motion.div
      key="create"
      variants={fadeSlideVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={springGentle}
      className="flex min-w-0 flex-col overflow-hidden"
    >
      <div className="flex flex-col items-center px-6 pb-1 pt-7">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 dark:bg-blue-950/40"
        >
          <LinkIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" strokeWidth={1.75} />
        </motion.div>

        <DialogHeader className="mt-3 items-center text-center">
          <DialogTitle className="text-[15px] font-medium tracking-[-0.01em]">{title}</DialogTitle>
          <DialogDescription className="mt-0.5 text-[13px] leading-relaxed text-muted-foreground/80">
            {description}
          </DialogDescription>
        </DialogHeader>
      </div>

      <div className="px-6 pb-1 pt-4">
        <Input
          id="session-name"
          placeholder={namePlaceholder}
          value={name}
          onChange={handleSessionNameChange}
          maxLength={512}
        />
      </div>

      <div className="flex flex-col px-3 py-1.5">{settings}</div>

      <div className="flex items-center justify-between border-t border-gray-100 px-6 py-3 dark:border-gray-800">
        {hasExistingSessions ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-[13px] text-muted-foreground hover:text-foreground"
            onClick={onBack}
          >
            ← Back
          </Button>
        ) : (
          <DialogClose asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-[13px] text-muted-foreground hover:text-foreground"
            >
              Cancel
            </Button>
          </DialogClose>
        )}
        <Button
          size="sm"
          disabled={isCreateDisabled}
          onClick={onCreate}
          className="gap-1.5 rounded-lg bg-blue-600 px-5 text-[13px] font-medium text-white shadow-none hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
        >
          {isPending ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={CREATE_SPINNER_TRANSITION}
              className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white"
            />
          ) : (
            <LinkIcon className="h-3.5 w-3.5" />
          )}
          {isPending ? pendingButtonLabel : "Create link"}
        </Button>
      </div>
    </motion.div>
  );
};
